import styled from '@emotion/styled';
import { rem } from '@utils';

const Wrapper = styled.div`
  .button {
    white-space: nowrap;
  }
`;

const Menu = styled.menu<{ hidden?: boolean }>(
  ({ hidden, theme }) => `
  background: ${theme.palette.common.white};
  border-radius: 4px;
  box-shadow: 0px 2px 14px 0px rgba(0, 0, 0, 0.08);
  cursor: default;
  display: flex;
  flex-flow: column;
  overflow-x: hidden;
  max-height: var(--prompt-menu-max-height);
  opacity: ${hidden ? 0 : 1};
  padding: ${rem(4)} 0;
  position: absolute;
  pointer-events: ${hidden ? 'none' : 'auto'};
  transform: scale(${hidden ? 0 : 1});
  transition: 
    opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) ${hidden ? 0 : 200}ms;
  width: 100%;
  will-change: opacity, transform;
  z-index: ${hidden ? -1 : 2};
`
);

const MenuLabel = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
  font-size: ${rem(14)};
  font-weight: 500;
  margin: ${rem(16)} 0;
  padding: 0 ${rem(16)};
`;

const List = styled.ul<{ alwaysShowScrollbar?: boolean; scrollable?: boolean }>`
  margin-right: ${({ scrollable }) => rem(scrollable ? 4 : 0)};
  overflow-y: ${({ alwaysShowScrollbar, scrollable }) => {
    if (scrollable) {
      if (alwaysShowScrollbar) {
        return 'visible';
      }

      return 'auto';
    }

    return 'hidden';
  }};
  overflow-x: hidden;

  &&::-webkit-scrollbar {
    width: ${rem(8)};
  }

  &&::-webkit-scrollbar-thumb {
    border-width: ${rem(2)};
  }
`;

const ListItem = styled.li<{ selected?: boolean }>(
  ({ selected, theme }) => `
  color: ${theme.palette.common.black};
  cursor: ${selected ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  flex-flow: row nowrap;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(16)};
  user-select: none;

  .icon {
    flex-shrink: 0;
  }

  *:is(${ListItemIcon}, ${ListItemTitle}, ${ListItemSubtitle}) {
    transition: color 100ms ease;
  }

  *:is(${ListItemIcon}, ${ListItemTitle}) {
    color: ${selected ? theme.palette.primary.dark : 'inherit'};
  }

  &:hover *:is(${ListItemIcon}, ${ListItemTitle}, ${ListItemSubtitle}) {
    ${!selected && `color: ${theme.palette.primary.main};`}
  }

  &:hover .action-icon {
    color: ${theme.palette.primary.main};
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.dark};
    outline-offset: -2px;
  }

  &.virtual-focus {
    background: ${theme.palette.pink.bg};
    
    * {
      color: ${theme.palette.primary.main};
    }
  }
`
);

const ListItemIcon = styled.div`
  flex-shrink: 0;
`;

const ListItemTitle = styled.div`
  font-weight: 600;
`;

const ListItemSubtitle = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
`;

const ListItemContent = styled.div`
  flex-grow: 1;
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  overflow: hidden;
  white-space: nowrap;

  * {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ListItemSkeleton = styled.li(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(16)};
  height: ${rem(48)};

  &::before {
    background: linear-gradient(90deg, ${theme.palette.grey['300']}, ${theme.palette.grey['200']});
    border-radius: 50%;
    content: '';
    display: block;
    width: 16px;
    height: 16px;
  }

  ${ListItemContent}::before, ${ListItemContent}::after {
    background: linear-gradient(
      90deg,
      ${theme.palette.grey['200']},
      ${theme.palette.grey['300']},
      ${theme.palette.grey['200']}
    );
    border-radius: 2px;
    height: 10px;
    content: '';
    display: block;
  }
    
  ${ListItemContent}::before {
    width: 50%;
    max-width: ${rem(200)};
    margin-bottom: ${rem(4)};
  }
  
  ${ListItemContent}::after {
    width: 25%;
    max-width: ${rem(100)};
  }
`
);

const Text = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
  padding: ${rem(8)} ${rem(16)};

  b {
    font-weight: 600;
  }
`;

const SearchInputWrapper = styled.div(
  ({ theme }) => `
  background: ${theme.palette.grey['100']};
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  margin: ${rem(16)} ${rem(16)} ${rem(8)};
  position: relative;
  padding: 0 ${rem(12)};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: ${theme.palette.grey['400']};
    transition: 200ms ease;
  }

  &:focus-within::after {
    background: ${theme.palette.primary.dark};
    height: 2px;
  }
`
);

const SearchInput = styled.input(
  ({ theme }) => `
  background: transparent;
  border: none;
  color: ${theme.palette.common.black};
  font-size: ${rem(14)};
  font-family: inherit;
  font-weight: 600;
  line-height: ${rem(16)};
  height: ${rem(38)};
  width: 100%;

  &::placeholder {
    color: ${theme.palette.grey['600']};
    font-weight: 400;
  }
  
  &:focus {
    outline: none;
  }

  @media (max-width: 430px) {
    // No auto-zoom on iOS devices
    font-size: ${rem(16)};
  }
`
);

const ResetButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  width: ${rem(16)};
  height: ${rem(16)};
  transition: color 100ms ease;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &:focus-visible {
    border-radius: 8px;
    outline: 2px solid ${({ theme }) => theme.palette.primary.dark};
    outline-offset: 1px;
  }
`;

export default {
  List,
  ListItem,
  ListItemIcon,
  ListItemTitle,
  ListItemSubtitle,
  ListItemContent,
  ListItemSkeleton,
  Menu,
  MenuLabel,
  SearchInputWrapper,
  SearchInput,
  ResetButton,
  Text,
  Wrapper
};
